import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { IAuthUser } from '@today/types'
import { AuthErrorCode } from '@today/types'
import { isAxiosError } from 'axios'
import { createContext, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { FORCED_MFA_ENROLMENT_PATH } from '@/auth/forced-mfa-enrolment'
import { authService } from '@/services/auth.service'

export const AUTH_QUERY_KEY = ['auth', 'me'] as const

export interface AuthContextValue {
	user: IAuthUser | null
	isAuthenticated: boolean
	isLoading: boolean
	/** Start the Cognito hosted-UI login redirect (single SPA client). */
	login: () => void
	logout: () => void
	/**
	 * Re-read the access token and (re-)fetch `/auth/me`. Call this right after a
	 * token is written to localStorage (the OIDC callback) so the provider picks
	 * up the new token immediately — the `/auth/me` query is `enabled: hasToken`,
	 * which is only re-evaluated on a provider re-render, so a fresh token needs
	 * this nudge or the dashboard bounces back to /login on first login.
	 */
	refreshAuth: () => void
}

export const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * The access token is persisted in `localStorage` so the login survives tab
 * close and browser restarts — `sessionStorage` would be per-tab and cleared
 * on close, which forces users to re-authenticate on every visit. The
 * long-term plan is to move the token off the JS-accessible surface entirely
 * via an HttpOnly+Secure+SameSite cookie set by a backend-for-frontend (BFF)
 * variant of `/auth/callback`; this localStorage step is the pragmatic
 * middle ground until that lands.
 */
function readAccessToken(): string | null {
	return localStorage.getItem('accessToken')
}

/**
 * Mirrors the error-handling rules locked in by `auth-context-provider`'s
 * spec: 401 / 403 clear the token; ACCOUNT_INACTIVE redirects to /error/inactive;
 * a bare 401 redirects to /error/unauthorized; network/5xx errors fall through
 * (token preserved, no redirect) so transient failures don't sign the user out.
 */
function handleAuthQueryError(error: unknown): void {
	if (!isAxiosError(error)) return

	const status = error.response?.status
	const errBody = error.response?.data as { error?: { code?: string } } | undefined
	const errorCode = errBody?.error?.code

	// A required-but-unenrolled user (TO-591): the session is VALID, they just must
	// enrol first. Keep the token (enrolment needs it) and route to the enrolment
	// entry — do NOT treat it as a logout, which would clear the token and bounce
	// them to /login, recreating the deadlock.
	if (errorCode === AuthErrorCode.MFA_ENROLMENT_REQUIRED) {
		window.location.href = FORCED_MFA_ENROLMENT_PATH
		return
	}

	if (status === 401 || status === 403) {
		localStorage.removeItem('accessToken')
	}

	if (errorCode === AuthErrorCode.ACCOUNT_INACTIVE) {
		window.location.href = '/error/inactive'
		return
	}

	if (status === 401) {
		window.location.href = '/error/unauthorized'
	}
}

interface AuthProviderProps {
	children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
	const queryClient = useQueryClient()
	// `tokenVersion` lets `logout` (and future token rotations) re-evaluate the
	// query's `enabled` flag without going through cache invalidation alone.
	const [tokenVersion, setTokenVersion] = useState(0)
	const hasToken = readAccessToken() !== null

	const { data, isLoading, isFetching, error } = useQuery<IAuthUser>({
		queryKey: [...AUTH_QUERY_KEY, tokenVersion],
		queryFn: () => {
			const token = readAccessToken()
			if (!token) throw new Error('No access token')
			return authService.getCurrentUser(token)
		},
		enabled: hasToken,
		staleTime: Infinity,
		retry: false,
	})

	useEffect(() => {
		if (error) handleAuthQueryError(error)
	}, [error])

	const login = useCallback(() => {
		void authService.initiateLogin()
	}, [])

	const logout = useCallback(() => {
		localStorage.removeItem('accessToken')
		localStorage.removeItem('originalAccessToken')
		queryClient.removeQueries({ queryKey: AUTH_QUERY_KEY })
		setTokenVersion((v) => v + 1)
		void authService.logout()
	}, [queryClient])

	// Bump tokenVersion so the provider re-reads the token, re-enabling +
	// refetching the `/auth/me` query with the just-stored access token.
	const refreshAuth = useCallback(() => {
		setTokenVersion((v) => v + 1)
	}, [])

	const user = data ?? null

	const value = useMemo<AuthContextValue>(
		() => ({
			user,
			isAuthenticated: user !== null,
			isLoading: hasToken && (isLoading || isFetching) && !user && !error,
			login,
			logout,
			refreshAuth,
		}),
		[user, hasToken, isLoading, isFetching, error, login, logout, refreshAuth],
	)

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
